import { Box, css, styled } from "@mui/material";
import { glMatrix } from "gl-matrix";
import { RefCallback, useCallback, useEffect, useRef, useState } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { LinearProgress, Loading } from "components";
import { explorerGlobalsActions, useExplorerGlobals } from "contexts/explorerGlobals";
import { useLoadAlignments } from "features/alignment/hooks/useLoadAlignments";
import { useHandleClipping } from "features/clippingPlanes";
import { useHandleDeviations } from "features/deviations";
import { useHandleDitioAuth } from "features/ditio";
import { Engine2D } from "features/engine2D/engine2D";
import { Engine2DHtmlInteractions } from "features/engine2D/engine2DHtmlInteractions";
import { Engine2DInteractions } from "features/engine2D/engine2DInteractions";
import { useRenderLocationFormAssets } from "features/forms/hooks/useRenderLocationFormAssets";
import { useHandleImages } from "features/images";
import { useHandleJiraKeepAlive } from "features/jira";
import { useHandleManhole } from "features/manhole";
import { useHandleLocationMarker } from "features/myLocation";
import { useHandleOffline } from "features/offline";
import { useHandleCrossSection } from "features/orthoCam";
import { useHandleOutlineLasers } from "features/outlineLaser";
import { PerformanceStats } from "features/performanceStats";
import { useHandleUrlSearch } from "features/search";
import { useHandleXsiteManageKeepAlive, useHandleXsiteManageMachineLocations } from "features/xsiteManage";
import { useGlobalShortcuts } from "hooks/useGlobalShortcuts";
import { useIntercom } from "hooks/useIntercom";
import { useRedirectWheelEvents } from "hooks/useRedirectWheelEvents";
import { useRefreshProjectPermissions } from "hooks/useRefreshProjectPermissions";
import { UxFlavor } from "slices/explorer/types";
import { AsyncStatus } from "types/misc";

import { ELEMENT_IDS } from "../bookmarks/utils";
import { useAutofreeze } from "./hooks/useAutofreeze";
import { useCanvasClickHandler } from "./hooks/useCanvasClickHandler";
import { useCanvasEventHandlers } from "./hooks/useCanvasEventHandlers";
import { useHandleAdvancedSettings } from "./hooks/useHandleAdvancedSettings";
import { useHandleBackground } from "./hooks/useHandleBackground";
import { useHandleCameraMoved } from "./hooks/useHandleCameraMoved";
import { useHandleCameraSpeed } from "./hooks/useHandleCameraSpeed";
import { useHandleCameraState } from "./hooks/useHandleCameraState";
import { useHandleCanvasCursor } from "./hooks/useHandleCanvasCursor";
import { useHandleClippingOutlines } from "./hooks/useHandleClippingOutlines";
import { useHandleGrid } from "./hooks/useHandleGrid";
import { useHandleHighlights } from "./hooks/useHandleHighlights";
import { useHandleInit } from "./hooks/useHandleInit";
import { useHandleInitialBookmark } from "./hooks/useHandleInitialBookmark";
import { useHandlePointVisualization } from "./hooks/useHandlePointVisualization";
import { useHandleSubtrees } from "./hooks/useHandleSubtrees";
import { useHandleTerrain } from "./hooks/useHandleTerrain";
import { useStableViewportFix } from "./hooks/useStableViewportFix";
import { useSyncDeviceProfile } from "./hooks/useSyncDeviceProfile";
import { useUpdateTransparentSelectionBase } from "./hooks/useUpdateTransparentSelectionBase";
import { Images } from "./images";
import { Markers } from "./markers";
import { selectDebugStats, selectLoadingHandles, selectSceneStatus } from "./renderSlice";
import { SceneError } from "./sceneError";
import { SelectBox } from "./selectBox";
import { Stamp } from "./stamp";

glMatrix.setMatrixArrayType(Array);

const Canvas = styled("canvas")(
    () => css`
        outline: 0;
        touch-action: none;
        -webkit-touch-callout: none;
        height: 100%;
        width: 100%;
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 0;
    `,
);

const Svg = styled("svg")(
    () => css`
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
        pointer-events: none;
        g {
            will-change: transform;
        }
    `,
);

export function Render3D({ uxFlavor }: { uxFlavor?: UxFlavor }) {
    const {
        state: { view, canvas, size },
        dispatch: dispatchGlobals,
    } = useExplorerGlobals();

    const loadingHandles = useAppSelector(selectLoadingHandles);
    const sceneStatus = useAppSelector(selectSceneStatus);
    const debugStats = useAppSelector(selectDebugStats);

    const [svg, setSvg] = useState<null | SVGSVGElement>(null);
    const [htmlInteractionContainer, setHtmlInteractionContainer] = useState<null | { update: () => void }>(null);
    const [markersContainer, setMarkersContainer] = useState<null | { update: () => void }>(null);

    const engine2dRenderFnRef = useRef<((moved: boolean, idleFrame?: boolean) => void) | undefined>();
    const pointerPosRef = useRef([0, 0] as [x: number, y: number]);
    const pointerDownStateRef = useRef<{
        timestamp: number;
        x: number;
        y: number;
    }>();
    const canvasRef: RefCallback<HTMLCanvasElement> = useCallback(
        (el) => {
            dispatchGlobals(explorerGlobalsActions.update({ canvas: el }));
        },
        [dispatchGlobals],
    );

    useEffect(() => {
        htmlInteractionContainer?.update();
    }, [size, htmlInteractionContainer]);

    useEffect(() => {
        markersContainer?.update();
    }, [size, markersContainer]);

    useHandleInit({ uxFlavor });
    useSyncDeviceProfile();
    useRefreshProjectPermissions();
    useHandleInitialBookmark();
    useHandleUrlSearch();
    useHandleCameraMoved({ engine2dRenderFnRef, containers: [htmlInteractionContainer, markersContainer] });
    useHandleCameraState();
    useHandleCameraSpeed();
    useHandleGrid();
    useHandleBackground();
    useHandleHighlights();
    useAutofreeze();
    useHandlePointVisualization();
    useHandleSubtrees();
    useHandleTerrain();
    useHandleAdvancedSettings();
    useHandleClipping();
    useHandleManhole();
    useHandleLocationMarker();
    useHandleCrossSection();
    useHandleDeviations();
    useHandleImages();
    useHandleOffline();
    useHandleClippingOutlines();
    useHandleOutlineLasers();
    useGlobalShortcuts();
    useUpdateTransparentSelectionBase();
    useStableViewportFix(uxFlavor);

    useHandleJiraKeepAlive();
    useHandleXsiteManageKeepAlive();
    useHandleXsiteManageMachineLocations();
    useHandleDitioAuth();

    // This is used by at least Deviations and Horizontal alignment and is installed on app start
    // There's AsyncState based request deduplication, but if multiple instances of this
    // hook execute in the same render frame (before state is updated in rtk store) -
    // they all use Initial state and all fire requests
    useLoadAlignments();

    useRenderLocationFormAssets();

    useIntercom();

    const cursor = useHandleCanvasCursor();
    const redirectWheelToCanvas = useRedirectWheelEvents();
    const eventHandlers = useCanvasEventHandlers({
        pointerPosRef,
        cursor,
        svg,
        pointerDownStateRef,
        engine2dRenderFnRef,
    });
    const onClick = useCanvasClickHandler({
        pointerDownStateRef,
        lastSelectBoxCompletionTime: eventHandlers.lastSelectBoxCompletionTime,
    });

    return (
        <Box
            id={ELEMENT_IDS.renderContainer}
            position="relative"
            width="100%"
            height="100%"
            sx={{ userSelect: "none", overflow: "hidden" }}
        >
            {loadingHandles.length !== 0 && (
                <Box position={"absolute"} top={0} width={1} display={"flex"} justifyContent={"center"}>
                    <LinearProgress />
                </Box>
            )}
            <Canvas id={ELEMENT_IDS.mainCanvas} onClick={onClick} {...eventHandlers} tabIndex={1} ref={canvasRef} />
            {sceneStatus.status === AsyncStatus.Error && <SceneError />}
            {[AsyncStatus.Initial, AsyncStatus.Loading].includes(sceneStatus.status) && <Loading />}
            {sceneStatus.status === AsyncStatus.Success && view && canvas && (
                <>
                    {debugStats.enabled && <PerformanceStats />}
                    <Engine2D
                        onMeasureUpdate={htmlInteractionContainer ? () => htmlInteractionContainer.update() : undefined}
                        pointerPosRef={pointerPosRef}
                        renderFnRef={engine2dRenderFnRef}
                        svg={svg}
                        onWheelCapture={redirectWheelToCanvas}
                    />
                    <Stamp />
                    <SelectBox />
                    <Svg width={size.width} height={size.height} ref={setSvg}>
                        <Markers ref={setMarkersContainer} />
                        <Engine2DInteractions />
                        <g id="cursor" />
                        <g id="camera-cursor" />
                    </Svg>
                    <div style={{ width: size.width, height: size.height }}>
                        <Engine2DHtmlInteractions ref={setHtmlInteractionContainer} />
                    </div>
                    <Images />
                </>
            )}
        </Box>
    );
}
